/**
 * Easy Email Pro MCP Server
 * 提供邮件模板生成和转换的 MCP 工具
 */

import { Server } from "@modelcontextprotocol/sdk/server/index.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import {
  CallToolRequestSchema,
  GetPromptRequestSchema,
  ListPromptsRequestSchema,
  ListToolsRequestSchema,
} from "@modelcontextprotocol/sdk/types.js";

import {
  generateEmailTemplate,
  GENERATE_EMAIL_TEMPLATE_TOOL,
  convertToMjml,
  CONVERT_TO_MJML_TOOL,
  convertToHtml,
  CONVERT_TO_HTML_TOOL,
  getGuidelines,
  GET_GUIDELINES_TOOL,
  getElementSchema,
  GET_ELEMENT_SCHEMA_TOOL,
  generateSection,
  GENERATE_SECTION_TOOL,
  assemblePage,
  ASSEMBLE_PAGE_TOOL,
  extractPlaceholderImages,
  replaceImageUrls,
  EXTRACT_PLACEHOLDER_IMAGES_TOOL,
  REPLACE_IMAGE_URLS_TOOL,
  convertBlockToImage,
  CONVERT_BLOCK_TO_IMAGE_TOOL,
} from "./tools";

import { imageAnalysisGuidePrompt } from "./prompts";

import type { EmailStructurePayload, PageElement } from "./types";

/**
 * 创建并配置 MCP Server
 */
export function createMcpServer() {
  const server = new Server(
    {
      name: "easy-email-pro-mcp",
      version: "1.0.0",
    },
    {
      capabilities: {
        tools: {},
        prompts: {},
      },
    }
  );

  // 注册工具列表处理器
  server.setRequestHandler(ListToolsRequestSchema, async () => {
    return {
      tools: [
        GET_GUIDELINES_TOOL,
        GET_ELEMENT_SCHEMA_TOOL,
        GENERATE_SECTION_TOOL,
        ASSEMBLE_PAGE_TOOL,
        GENERATE_EMAIL_TEMPLATE_TOOL,
        CONVERT_TO_MJML_TOOL,
        CONVERT_TO_HTML_TOOL,
        CONVERT_BLOCK_TO_IMAGE_TOOL,
        EXTRACT_PLACEHOLDER_IMAGES_TOOL,
        REPLACE_IMAGE_URLS_TOOL,
        {
          name: "generate_complete_email",
          description: `一键生成完整的邮件 HTML。接收结构描述，依次执行：生成模板 JSON -> 转换为 MJML -> 转换为 HTML。

输入参数:
- structure: EmailStructureDescription 对象

输出:
- success: boolean
- template: 生成的模板 JSON
- mjml: MJML 字符串
- html: 最终的 HTML 字符串
- error: 错误信息（如果失败）`,
          inputSchema: {
            type: "object" as const,
            properties: {
              structure: {
                type: "object",
                description: "邮件结构描述",
              },
            },
            required: ["structure"],
          },
        },
      ],
    };
  });

  // 注册工具调用处理器
  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;

    try {
      switch (name) {
        case "get_guidelines": {
          const result = getGuidelines();
          return {
            content: [
              {
                type: "text",
                text: result.guidelines,
              },
            ],
          };
        }

        case "get_element_schema": {
          const elementType = args?.elementType as string | undefined;
          const result = getElementSchema({ elementType });
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "generate_section": {
          const section = args?.section as any;
          const result = generateSection({ section });
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "assemble_page": {
          const pageConfig = args?.pageConfig || {};
          const sections = args?.sections as any[];
          const result = assemblePage({ pageConfig, sections });
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "generate_email_template": {
          const structure = args?.structure as EmailStructurePayload;
          const result = generateEmailTemplate({ structure });
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "extract_placeholder_images": {
          const template = args?.template as any;
          const result = extractPlaceholderImages({ template });
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "replace_image_urls": {
          const template = args?.template as any;
          const replacements = args?.replacements as any[];
          const result = replaceImageUrls({ template, replacements });
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "convert_to_mjml": {
          const template = args?.template as PageElement;
          const mode = (args?.mode as "testing" | "production") || "production";
          const beautify = args?.beautify !== false;
          const displayMode =
            (args?.displayMode as
              | "desktop-mobile"
              | "only-desktop"
              | "only-mobile") || "desktop-mobile";

          const result = await convertToMjml(
            { template },
            { mode, beautify, displayMode }
          );
          return {
            content: [
              {
                type: "text",
                text: result.success
                  ? result.mjml!
                  : JSON.stringify({ error: result.error }),
              },
            ],
          };
        }

        case "convert_to_html": {
          const mjml = args?.mjml as string;
          const minify = args?.minify === true;
          const validationLevel =
            (args?.validationLevel as "strict" | "soft" | "skip") || "soft";
          const mergetagsData = args?.mergetagsData as
            | Record<string, any>
            | undefined;

          const result = convertToHtml(
            { mjml },
            { minify, validationLevel, mergetagsData }
          );
          return {
            content: [
              {
                type: "text",
                text: result.success
                  ? result.html!
                  : JSON.stringify({ error: result.error }),
              },
            ],
          };
        }

        case "convert_block_to_image": {
          const block = args?.block as any;
          const pageElement = args?.pageElement as PageElement | undefined;
          const width = args?.width as number | undefined;
          const height = args?.height as number | undefined;
          const format = (args?.format as "png" | "jpeg") || "png";
          const mode = (args?.mode as "testing" | "production") || "production";
          const displayMode =
            (args?.displayMode as
              | "desktop-mobile"
              | "only-desktop"
              | "only-mobile") || "desktop-mobile";
          const mergetagsData = args?.mergetagsData as
            | Record<string, any>
            | undefined;

          const result = await convertBlockToImage({
            block,
            pageElement,
            width,
            height,
            format,
            mode,
            displayMode,
            mergetagsData,
          });

          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(result, null, 2),
              },
            ],
          };
        }

        case "generate_complete_email": {
          const structure = args?.structure as EmailStructurePayload;

          // Step 1: 生成模板
          const templateResult = generateEmailTemplate({ structure });
          if (!templateResult.success) {
            return {
              content: [
                {
                  type: "text",
                  text: JSON.stringify({
                    success: false,
                    error: `Failed to generate template: ${templateResult.error}`,
                  }),
                },
              ],
            };
          }

          // Step 2: 转换为 MJML
          const mjmlResult = await convertToMjml({
            template: templateResult.template!,
          });
          if (!mjmlResult.success) {
            return {
              content: [
                {
                  type: "text",
                  text: JSON.stringify({
                    success: false,
                    template: templateResult.template,
                    error: `Failed to convert to MJML: ${mjmlResult.error}`,
                  }),
                },
              ],
            };
          }

          // Step 3: 转换为 HTML
          const htmlResult = convertToHtml({ mjml: mjmlResult.mjml! });
          if (!htmlResult.success) {
            return {
              content: [
                {
                  type: "text",
                  text: JSON.stringify({
                    success: false,
                    template: templateResult.template,
                    mjml: mjmlResult.mjml,
                    error: `Failed to convert to HTML: ${htmlResult.error}`,
                  }),
                },
              ],
            };
          }

          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(
                  {
                    success: true,
                    template: templateResult.template,
                    mjml: mjmlResult.mjml,
                    html: htmlResult.html,
                  },
                  null,
                  2
                ),
              },
            ],
          };
        }

        default:
          throw new Error(`Unknown tool: ${name}`);
      }
    } catch (error) {
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify({
              error: error instanceof Error ? error.message : String(error),
            }),
          },
        ],
        isError: true,
      };
    }
  });

  // 注册 Prompt 列表处理器
  server.setRequestHandler(ListPromptsRequestSchema, async () => {
    return {
      prompts: [
        {
          name: imageAnalysisGuidePrompt.name,
          description: imageAnalysisGuidePrompt.description,
        },
      ],
    };
  });

  // 注册 Prompt 获取处理器
  server.setRequestHandler(GetPromptRequestSchema, async (request) => {
    const { name } = request.params;

    if (name === imageAnalysisGuidePrompt.name) {
      return {
        description: imageAnalysisGuidePrompt.description,
        messages: [
          {
            role: "user",
            content: {
              type: "text",
              text: imageAnalysisGuidePrompt.content,
            },
          },
        ],
      };
    }

    throw new Error(`Unknown prompt: ${name}`);
  });

  return server;
}

/**
 * 启动 MCP Server（stdio 模式）
 */
export async function startMcpServer() {
  const server = createMcpServer();
  const transport = new StdioServerTransport();

  await server.connect(transport);

  console.error("Easy Email Pro MCP Server started");
}
